import React from 'react';
import { Box, Divider, Stack, Typography } from '@mui/material';
import logo from '../assets/images/transcriber-logo.svg';

const sectionHeading = {
  fontWeight: 700,
  mt: 2,
  mb: 1,
};

const paragraph = {
  color: 'text.primary',
  lineHeight: 1.4,
};

const listStyle = {
  color: 'text.primary',
  fontSize: '0.975rem',
  lineHeight: 1.8,
  m: 0
};

export default function PrivacyPolicyPage() {
  return (
    <Box sx={{ maxWidth: 1080, width: '90%', mx: 'auto' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          bgcolor: 'background.paper',
          // borderRadius: 3,
          // boxShadow: '0 20px 60px rgba(15, 23, 42, 0.08)',
          p: { xs: 4, md: 5 },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2} sx={{ flexWrap: 'wrap' }}>
          {/* <Box
            component="img"
            src={logo}
            alt="VENDX Genie logo"
            sx={{ height: 50, width: 'auto', display: 'block' }}
          /> */}
          <Box>
            <Typography variant="h4" component="h1" color='primary.main' sx={{ fontWeight: 700, }}>
              Privacy Policy
            </Typography>
            <Typography sx={{ color: 'grey.600', fontSize: '0.85rem' }}>
              Effective Date: 06/05/2026
            </Typography>
          </Box>
        </Stack>

        <Typography sx={paragraph}>
          This Privacy Policy describes how our application ("the App") collects,
          uses, and protects your information when you use our services.
        </Typography>

        <Divider />

        <Typography variant='h5' sx={sectionHeading}>1. Information We Collect</Typography>

        <Typography sx={{ ...paragraph, mb: 0 }}>
          <strong>A. Account Information</strong>
        </Typography>
        <Typography sx={{ ...paragraph, mb: 0 }}>
          We collect login information such as:
        </Typography>
        <Box component="ul" sx={listStyle}>
          <li>Email address</li>
          <li>Authentication credentials</li>
        </Box>
        <Typography sx={{ ...paragraph, mb: 3 }}>
          This is required to create and manage your account.
        </Typography>

        <Typography sx={{ ...paragraph, mt: 0 }}>
          <strong>B. Audio Data</strong>
        </Typography>
        <Box component="ul" sx={{ ...listStyle, mb: 3 }}>
          <li>The App allows users to record voice input using the device microphone.</li>
          <li>Audio recordings are stored locally on your device only.</li>
          <li>We do not store audio recordings on our servers.</li>
          <li>Audio is processed in real time for transcription using AI (LLM-based systems) and immediately discarded after processing.</li>
        </Box>

        <Typography sx={{ ...paragraph, mt: 0 }}>
          <strong>C. Transcription Data</strong>
        </Typography>
        <Box component="ul" sx={{ ...listStyle, mb: 3 }}>
          <li>Audio recordings are processed to generate transcripts and summaries.</li>
          <li>These outputs are stored on our servers unless deleted by the user.</li>
          <li>We do not use your audio, transcripts, or recordings to train AI models.</li>
        </Box>

        <Typography sx={{ ...paragraph, mt: 0 }}>
          <strong>D. Calendar Data</strong>
        </Typography>
        <Typography sx={{ ...paragraph, mb: 3 }}>
          If you choose to connect your calendar, we access calendar data only to enable
          transcription or summaries related to events. Calendar access is optional and
          user-controlled.
        </Typography>

        <Typography variant='h5' sx={sectionHeading}>2. Permissions We Use</Typography>
        <Typography sx={paragraph}>
          The App may request the following permissions:
        </Typography>
        <Box component="ul" sx={listStyle}>
          <li><strong>Microphone</strong>: To record audio for transcription</li>
          <li><strong>Storage</strong>: To save recordings locally on your device</li>
          <li><strong>Calendar Access</strong>: To integrate transcription features with your events</li>
        </Box>
        <Typography sx={{ ...paragraph, mb: 3 }}>
          Permissions are requested only when needed and can be revoked at any time via device settings.
        </Typography>

        <Typography variant='h5' sx={sectionHeading}>3. How We Use Information</Typography>
        <Typography sx={paragraph}>
          We use collected data to:
        </Typography>
        <Box component="ul" sx={listStyle}>
          <li>Provide transcription and summarization features</li>
          <li>Manage user accounts and authentication</li>
          <li>Enable calendar-based functionality (if connected)</li>
          <li>Store and retrieve transcripts</li>
          <li>Improve core app performance and reliability</li>
        </Box>
        <Typography sx={paragraph}>
          We do NOT:
        </Typography>
        <Box component="ul" sx={{ ...listStyle, mb: 3 }}>
          <li>Sell personal data</li>
          <li>Use data for advertising</li>
          <li>Train AI models on user data</li>
          <li>Build behavioral profiles</li>
          <li>Share data for marketing purposes</li>
        </Box>

        <Typography variant='h5' sx={sectionHeading}>4. Data Storage and Security</Typography>
        <Box component="ul" sx={{ ...listStyle, mb: 3 }}>
          <li>Audio recordings are <strong>never stored on our servers</strong>.</li>
          <li>Transcripts are stored securely on our servers unless deleted by the user.</li>
          <li>Deleted transcripts are moved to a recycle bin and permanently deleted within 30 days.</li>
          <li>We implement reasonable security measures to protect your data.</li>
        </Box>

        <Typography variant='h5' sx={sectionHeading}>5. Data Sharing</Typography>
        <Typography sx={paragraph}>
          We do <strong>not sell or share your personal data</strong>.
        </Typography>
        <Typography sx={paragraph}>
          We only share data with third-party service providers strictly for core functionality, such as:
        </Typography>
        <Box component="ul" sx={listStyle}>
          <li>AI transcription / LLM processing providers</li>
          <li>Cloud hosting and database providers</li>
        </Box>
        <Typography sx={paragraph}>
          These providers:
        </Typography>
        <Box component="ul" sx={{ ...listStyle, mb: 3 }}>
          <li>Only process data to provide the Service</li>
          <li>Do not use your data for training AI models (where supported by provider policies)</li>
          <li>Do not use your data for advertising or profiling</li>
        </Box>

        <Typography variant='h5' sx={sectionHeading}>6. User Control and Data Deletion</Typography>
        <Box component="ul" sx={{ ...listStyle, mb: 3 }}>
          <li>You can delete recordings directly from your device.</li>
          <li>You can delete transcripts at any time.</li>
          <li>Deleted transcripts are permanently removed after 30 days.</li>
          <li>You can delete your account and all associated data upon request.</li>
        </Box>

        <Typography variant='h5' sx={sectionHeading}>7. Children’s Privacy</Typography>
        <Typography sx={{ ...paragraph, mb: 3 }}>
          The App is not intended for children under the age of 13.
          We do not knowingly collect personal data from children.
        </Typography>

        <Typography variant='h5' sx={sectionHeading}>8. Changes to This Policy</Typography>
        <Typography sx={{ ...paragraph, mb: 3 }}>
          We may update this Privacy Policy from time to time.
          Changes will be reflected with an updated "Effective Date."
          We may notify users if changes are material.
        </Typography>

        <Typography variant='h5' sx={sectionHeading}>9. Contact Us</Typography>
        <Typography sx={{ ...paragraph, mb: 0 }}>
          If you have any questions or concerns, contact us at:
        </Typography>
        <Typography sx={{ ...paragraph, mb: 0 }}>
          <strong>Email:</strong> <a href="mailto:genie@mavenvista.com">genie@mavenvista.com</a>
        </Typography>
      </Box>
    </Box>
  );
}
